// Trigger sets — authored per report; see docs/builder-report-authoring-spec.md.
import type { TriggerQuestion } from "./types";
import {
  OCCUPIED,
  PLANT_PEDESTRIAN,
  MANUAL_HANDLING,
  SLIPS_TRIPS,
  HAVS,
  SILICA,
  UNDERGROUND_SERVICES,
  WAH,
  HOT_WORK_PERMIT_Q,
  RESCUE_Q,
  ACCESS_OPTIONS,
} from "./shared";

// Minted once in the spec for the noise reports; reuse the literal here.
const NOISE = "haz-noise-vibration-noise-exposure-above-action-value";

export const SITE_GENERAL_TRIGGERS: Record<string, TriggerQuestion[]> = {
  "site-setup-welfare": [
    {
      key: "excavation",
      label: "Will any ground be broken for fence posts, cabin bases or service connections?",
      kind: "yesno",
      effects: {
        yes: { hazardsOn: [UNDERGROUND_SERVICES], permitsAdd: ["Permit to dig"] },
        no: {
          hazardsOff: [UNDERGROUND_SERVICES],
          permitsRemove: ["Permit to dig"],
          emergencyOff: ["Underground services strike"],
          scopeLine: "No ground-breaking during set-up — posts and bases are ground-bearing and connections are surface-run, so a permit to dig and underground-services controls are not required for this task.",
        },
      },
      notSure: { hazardsOn: [UNDERGROUND_SERVICES], permitsAdd: ["Permit to dig"], failCheck: "Ground-breaking and underground-services status confirmed" },
      followUps: {
        when: "yes",
        questions: [
          {
            key: "catScan",
            label: "Have utility drawings and a CAT/Genny scan of post and base positions been completed?",
            kind: "yesno",
            effects: {
              yes: { scopeLine: "Utility drawings obtained and CAT/Genny scan of all post and base positions completed before breaking ground; permit to dig in place." },
              no: { failCheck: "Utility drawings and CAT/Genny scan confirmed" },
            },
            notSure: { failCheck: "Utility drawings and CAT/Genny scan confirmed" },
          },
        ],
      },
    },
    {
      key: "deliveries",
      label: "Will cabins, hoarding or fencing be delivered and positioned by vehicle or plant?",
      kind: "yesno",
      effects: {
        yes: { hazardsOn: [PLANT_PEDESTRIAN, MANUAL_HANDLING] },
        no: {
          hazardsOff: [PLANT_PEDESTRIAN],
          scopeLine: "No vehicle or plant deliveries during set-up — units are hand-carried/sited manually, so plant–pedestrian segregation is not required for this task.",
        },
      },
      notSure: { hazardsOn: [PLANT_PEDESTRIAN], failCheck: "Delivery and plant arrangements confirmed" },
      followUps: {
        when: "yes",
        questions: [
          {
            key: "unloadMethod",
            label: "How will cabins and heavy units be unloaded and positioned?",
            kind: "choice",
            options: ["Telehandler", "HIAB / lorry-loader crane", "Hand offload (light units only)"],
            effects: {
              Telehandler: { scopeLine: "Units unloaded and positioned with a telehandler by a competent operator, with a banksman marshalling." },
              "HIAB / lorry-loader crane": { scopeLine: "Units unloaded with a HIAB / lorry-loader crane by a competent operator, with a banksman and exclusion zone under the load." },
              "Hand offload (light units only)": { scopeLine: "Light units hand-offloaded with team lifts and handling aids; no mechanical lift required." },
            },
          },
        ],
      },
    },
    {
      key: "cuttingHoarding",
      label: "Will hoarding or fixings be cut or ground on site?",
      kind: "yesno",
      effects: {
        yes: { permitsAdd: ["Hot work permit"] },
        no: {
          permitsRemove: ["Hot work permit"],
          scopeLine: "Hoarding is delivered cut to size — no cutting or grinding on site, so a hot work permit is not required for this task.",
        },
      },
      followUps: { when: "yes", questions: [HOT_WORK_PERMIT_Q] },
    },
    {
      key: "welfareEvidence",
      label: "How is the day-one welfare provision sized and sourced (toilets, washing, drinking water, rest, drying)?",
      kind: "text",
      placeholder: "e.g. 2 toilets + 1 urinal, hot/cold wash unit, drying room, hired from X, sized for peak 12",
      requiredCheck: "Welfare provision sized to CDM Schedule 2 for the peak workforce",
      effects: { "*": { scopeLine: "Welfare to CDM Schedule 2 (in place before any trade arrives): {answer}." } },
    },
    {
      key: "perimeterSecurity",
      label: "How is the perimeter secured against children and trespass out of hours?",
      kind: "text",
      placeholder: "e.g. solid hoarding to road frontage, locked gates, plant immobilised, daily security check by site manager",
      requiredCheck: "Out-of-hours perimeter and child-access security arrangements stated",
      effects: { "*": { scopeLine: "Out-of-hours security: {answer}." } },
    },
    {
      key: "tempElectric",
      label: "Are temporary electrical connections assigned to a named competent electrician with certification?",
      kind: "yesno",
      effects: {
        yes: { scopeLine: "Temporary electrical connections made and certified by a named competent electrician; supply isolated and proved dead before work, RCD-protected on energising." },
        no: { failCheck: "Named competent electrician and certification for temporary supplies confirmed" },
      },
      notSure: { failCheck: "Named competent electrician and certification for temporary supplies confirmed" },
    },
  ],

  "occupied-premises": [
    {
      key: "occupied",
      label: "Will occupants, staff or the public be present during the works?",
      kind: "yesno",
      effects: {
        yes: { hazardsOn: [OCCUPIED, SLIPS_TRIPS], requireFields: ["nearbyTradesOrPublic"] },
        no: {
          hazardsOff: [OCCUPIED],
          scopeLine: "The premises are decanted and unoccupied for the works — occupant-interface controls are reduced, though segregation from any adjacent occupied areas is maintained.",
        },
      },
      notSure: { hazardsOn: [OCCUPIED], failCheck: "Occupancy during the works confirmed" },
    },
    {
      key: "fireRoutes",
      label: "Will occupant fire-escape routes, alarm coverage or escape lighting be affected by the works?",
      kind: "yesno",
      effects: {
        yes: { scopeLine: "Works affect occupant escape provision — a signed alternative route, maintained alarm coverage and escape lighting are agreed with the occupier's responsible person before work." },
        no: { scopeLine: "Occupant fire-escape routes, alarm coverage and escape lighting are unaffected and remain fully available throughout the works." },
      },
      notSure: { failCheck: "Effect on occupant escape routes and alarm coverage confirmed with the occupier" },
    },
    {
      key: "dustNoise",
      label: "Will the works generate dust, fumes or substances that could reach occupied areas?",
      kind: "yesno",
      effects: {
        yes: { hazardsOn: ["haz-coshh-coshh-hazardou-substance-exposure"] },
        no: {
          hazardsOff: ["haz-coshh-coshh-hazardou-substance-exposure"],
          scopeLine: "No dust, fume or substance generation that could reach occupants — COSHH migration controls are not required for this task.",
        },
      },
      notSure: { hazardsOn: ["haz-coshh-coshh-hazardou-substance-exposure"], failCheck: "Dust/fume migration to occupied areas confirmed" },
    },
    {
      key: "safeguarding",
      label: "Is this a school, care or other safeguarding setting?",
      kind: "yesno",
      effects: {
        yes: { hazardsOn: ["haz-site-occupied-safeguarding-failure-in-sensitive-setting"], scopeLine: "Safeguarding setting — DBS where required by the client, no unsupervised lone contact with children or vulnerable adults, and the occupier's safeguarding policy briefed." },
        no: {
          hazardsOff: ["haz-site-occupied-safeguarding-failure-in-sensitive-setting"],
          competenceOff: ["Enhanced DBS"],
          emergencyOff: ["Safeguarding concern"],
          scopeLine: "Not a safeguarding setting — DBS and safeguarding controls are not required for this task.",
        },
      },
      notSure: { hazardsOn: ["haz-site-occupied-safeguarding-failure-in-sensitive-setting"], failCheck: "Safeguarding requirement for the setting confirmed" },
    },
    {
      key: "outOfHours",
      label: "Will any work be done out of hours or when the building is closed?",
      kind: "yesno",
      effects: {
        yes: { hazardsOn: ["haz-site-occupied-out-of-hours-working-in-occupied-building"], permitsAdd: ["Out-of-hours working permit"] },
        no: {
          hazardsOff: ["haz-site-occupied-out-of-hours-working-in-occupied-building"],
          permitsRemove: ["Out-of-hours working permit"],
          sequenceOff: [7],
          scopeLine: "All work is during agreed occupied hours — no out-of-hours working, so out-of-hours access and lone check-in controls are not required for this task.",
        },
      },
      notSure: { hazardsOn: ["haz-site-occupied-out-of-hours-working-in-occupied-building"], failCheck: "Out-of-hours working status confirmed" },
    },
    {
      key: "hotWorks",
      label: "Will any hot work (cutting, grinding, soldering) be done near occupants?",
      kind: "yesno",
      effects: {
        yes: { permitsAdd: ["Hot work permit"] },
        no: { permitsRemove: ["Hot work permit"], scopeLine: "No hot work near occupants — a hot work permit is not required for this task." },
      },
      notSure: { permitsAdd: ["Hot work permit"], failCheck: "Hot work requirement confirmed" },
      followUps: { when: "yes", questions: [HOT_WORK_PERMIT_Q] },
    },
    {
      key: "occupierLiaison",
      label: "Who is the named occupier contact and what notice/exclusion arrangements are agreed?",
      kind: "text",
      placeholder: "e.g. site manager liaises with facilities lead J Patel; 48h notice of disruptive work; zip-screen exclusion zone",
      requiredCheck: "Named occupier liaison and agreed exclusion/notice arrangements recorded",
      effects: { "*": { scopeLine: "Occupier liaison: {answer}." } },
    },
  ],

  "lone-working": [
    {
      key: "suitability",
      label: "Has the specific task and location been assessed as suitable for lone working (not on the prohibited list)?",
      kind: "yesno",
      effects: {
        yes: { scopeLine: "The task and location have been assessed as suitable for lone working and confirmed not on the prohibited-alone list." },
        no: { hazardsOn: ["haz-lone-working-high-risk-task-attempted-alone"], failCheck: "Lone-working suitability assessment completed and task confirmed not prohibited" },
      },
      notSure: { hazardsOn: ["haz-lone-working-high-risk-task-attempted-alone"], failCheck: "Lone-working suitability assessment completed and task confirmed not prohibited" },
    },
    {
      key: "highRiskAlone",
      label: "Could the task involve live electrical work, confined-space entry or rescue-impossible work at height?",
      kind: "yesno",
      effects: {
        yes: { hazardsOn: ["haz-lone-working-high-risk-task-attempted-alone"], scopeLine: "Task may touch a never-alone activity — a second competent person or planned rescue is required before that element proceeds; it is not done solo." },
        no: {
          hazardsOff: ["haz-lone-working-high-risk-task-attempted-alone"],
          scopeLine: "No live electrical, confined-space or rescue-impossible at-height element — those never-alone tasks are excluded from this lone-working package.",
        },
      },
      notSure: { hazardsOn: ["haz-lone-working-high-risk-task-attempted-alone"], failCheck: "Presence of any never-alone task element confirmed" },
    },
    {
      key: "publicFacing",
      label: "Could the worker face members of the public or aggression at the location?",
      kind: "yesno",
      effects: {
        yes: { hazardsOn: ["haz-lone-working-violence-or-aggression-toward-worker"] },
        no: {
          hazardsOff: ["haz-lone-working-violence-or-aggression-toward-worker"],
          emergencyOff: ["Violence or aggression"],
          scopeLine: "No public interface or known aggression risk at the location — violence-and-aggression controls are not required for this task.",
        },
      },
      notSure: { hazardsOn: ["haz-lone-working-violence-or-aggression-toward-worker"], failCheck: "Public-facing / aggression risk confirmed" },
    },
    {
      key: "remoteSignal",
      label: "Is the location remote or likely to have poor mobile signal?",
      kind: "yesno",
      effects: {
        yes: { hazardsOn: ["haz-lone-working-inability-to-summon-help-remote-location"], scopeLine: "Remote or poor-signal location — coverage verified before dispatch, check-in intervals shortened, and a satellite or radio backup provided." },
        no: {
          hazardsOff: ["haz-lone-working-inability-to-summon-help-remote-location"],
          scopeLine: "Location has confirmed reliable mobile coverage — a communications backup is not required for this task.",
        },
      },
      notSure: { hazardsOn: ["haz-lone-working-inability-to-summon-help-remote-location"], failCheck: "Communications coverage at the location confirmed" },
    },
    {
      key: "handling",
      label: "Will the worker handle loads that would normally need two people?",
      kind: "yesno",
      effects: {
        yes: { hazardsOn: [MANUAL_HANDLING], scopeLine: "Any two-person load is rescheduled for a team or broken down — solo handling is limited to assessed single-person loads with mechanical aids." },
        no: { hazardsOff: [MANUAL_HANDLING], scopeLine: "No loads requiring two people — solo manual handling is within single-person limits, so two-person handling controls are not required for this task." },
      },
      notSure: { hazardsOn: [MANUAL_HANDLING], failCheck: "Solo manual-handling load limits confirmed" },
    },
    {
      key: "deviceChoice",
      label: "What monitoring technology will the worker carry?",
      kind: "choice",
      options: ["Monitored lone-worker device (man-down + panic + GPS)", "App check-in with man-down", "Phone-call check-in only"],
      effects: {
        "Monitored lone-worker device (man-down + panic + GPS)": { scopeLine: "Monitoring: dedicated lone-worker device with automatic man-down, manual panic and GPS, monitored at the agreed end." },
        "App check-in with man-down": { scopeLine: "Monitoring: phone app with timed check-ins and man-down, monitored at the agreed end." },
        "Phone-call check-in only": { scopeLine: "Monitoring: scheduled phone-call check-ins only — used solely where coverage is confirmed and no man-down/panic risk is present." },
      },
    },
    {
      key: "checkInEvidence",
      label: "What is the check-in interval and who is the named monitor that receives it?",
      kind: "text",
      placeholder: "e.g. check-in every 2h to controller S Khan; missed check-in escalates after 30 min",
      requiredCheck: "Check-in interval and named monitor with escalation recorded",
      effects: { "*": { scopeLine: "Check-in regime: {answer}." } },
    },
  ],

  "noise-vibration": [
    {
      key: "vibratingTools",
      label: "Will vibrating hand tools (breakers, grinders, drills, compactors) be used?",
      kind: "yesno",
      effects: {
        yes: { hazardsOn: [HAVS, "haz-noise-vibration-exceeding-daily-vibration-limit-value", "haz-noise-vibration-cold-and-grip-force-increasing-vibration-effect"] },
        no: {
          hazardsOff: [HAVS, "haz-noise-vibration-exceeding-daily-vibration-limit-value", "haz-noise-vibration-cold-and-grip-force-increasing-vibration-effect"],
          emergencyOff: ["Early HAVS"],
          scopeLine: "No vibrating hand tools in use — hand-arm vibration controls are not required for this task; noise controls still apply where powered tools are used.",
        },
      },
      notSure: { hazardsOn: [HAVS, "haz-noise-vibration-exceeding-daily-vibration-limit-value"], failCheck: "Use of vibrating hand tools confirmed" },
      followUps: {
        when: "yes",
        questions: [
          {
            key: "toolRegister",
            label: "List the tools and their quoted vibration magnitudes (m/s²) so the A(8) can be checked",
            kind: "text",
            placeholder: "e.g. Breaker 12 m/s², angle grinder 5 m/s², SDS drill 9 m/s²",
            requiredCheck: "Tool register with vibration magnitudes recorded",
            effects: { "*": { scopeLine: "Tool register with vibration magnitudes: {answer}. Trigger-time budgets calculated from these against the 2.5 m/s² action value." } },
          },
        ],
      },
    },
    {
      key: "noiseTools",
      label: "Will the work create noise at or above the action values (breaking, grinding, cutting)?",
      kind: "yesno",
      effects: {
        yes: { hazardsOn: [NOISE] },
        // Noise is this report's headline hazard — "no" narrows the controls
        // (no hearing-protection zones) but the assessment hazard stays in
        // scope so the document never empties.
        no: {
          scopeLine: "Work is assessed as staying below the lower noise action value of 80 dB(A) — designated hearing-protection zones are not required; the assessment is reviewed if methods or plant change.",
        },
      },
      notSure: { hazardsOn: [NOISE], failCheck: "Noise exposure against the action values confirmed" },
    },
    {
      key: "cutting",
      label: "Will materials be cut or ground in a way that produces respirable dust or silica?",
      kind: "yesno",
      effects: {
        yes: { hazardsOn: [SILICA] },
        no: {
          hazardsOff: [SILICA],
          scopeLine: "No cutting or grinding producing respirable dust — silica/dust controls are not required for this task.",
        },
      },
      notSure: { hazardsOn: [SILICA], failCheck: "Dust/silica generation confirmed" },
    },
    {
      key: "healthSurveillance",
      label: "Are exposed operatives enrolled in HAVS and audiometric health surveillance?",
      kind: "yesno",
      effects: {
        yes: { scopeLine: "Exposed operatives enrolled in tiered HAVS and audiometric health surveillance, with a route to act on early findings." },
        no: { failCheck: "Health surveillance for exposed operatives confirmed and named" },
      },
      notSure: { failCheck: "Health surveillance for exposed operatives confirmed and named" },
    },
  ],

  "temporary-works": [
    {
      key: "twcEvidence",
      label: "Who is the appointed Temporary Works Coordinator (name / role)?",
      kind: "text",
      placeholder: "e.g. A Brown, TWC (CITB TWC trained)",
      requiredCheck: "Named Temporary Works Coordinator recorded",
      effects: { "*": { scopeLine: "Appointed Temporary Works Coordinator: {answer}, with authority to halt work that falls below standard." } },
    },
    {
      key: "designCategory",
      label: "What is the highest design-check category among the temporary works items?",
      kind: "choice",
      options: ["Category 0 — standard solution", "Category 1 — independent check, same firm", "Category 2 — separate team, same organisation", "Category 3 — independent organisation"],
      effects: {
        "Category 0 — standard solution": { scopeLine: "Highest design-check category: 0 — restricted to standard solutions and simple items checked by the designer." },
        "Category 1 — independent check, same firm": { scopeLine: "Highest design-check category: 1 — checked by someone independent of the designer within the same firm." },
        "Category 2 — separate team, same organisation": { scopeLine: "Highest design-check category: 2 — checked by a separate team within the same organisation." },
        "Category 3 — independent organisation": { scopeLine: "Highest design-check category: 3 — checked by a wholly independent organisation for the most complex/high-risk works." },
      },
    },
    {
      key: "atHeight",
      label: "Will any temporary works be erected or struck at height?",
      kind: "yesno",
      effects: {
        yes: { hazardsOn: [WAH] },
        no: {
          hazardsOff: [WAH],
          emergencyOff: ["Fall during erection"],
          scopeLine: "All temporary works are erected and struck at ground level — work-at-height controls are not required for this task.",
        },
      },
      notSure: { hazardsOn: [WAH], failCheck: "Work-at-height during erection/striking confirmed" },
      followUps: {
        when: "yes",
        questions: [
          {
            key: "accessMethod",
            label: "What access will be used for erecting/striking at height?",
            kind: "choice",
            options: ACCESS_OPTIONS,
            effects: Object.fromEntries(ACCESS_OPTIONS.map((o) => [o, { scopeLine: `Access for erecting/striking at height: ${o}.` }])),
          },
          RESCUE_Q,
        ],
      },
    },
    {
      key: "handling",
      label: "Will heavy props, soldiers, panels or shoring be handled?",
      kind: "yesno",
      effects: {
        yes: { hazardsOn: [MANUAL_HANDLING] },
        no: { hazardsOff: [MANUAL_HANDLING], scopeLine: "Components are light or mechanically handled — manual-handling controls are not required for this task." },
      },
      notSure: { hazardsOn: [MANUAL_HANDLING], failCheck: "Manual handling of components confirmed" },
    },
    {
      key: "permitControl",
      label: "Are the permits to load and to strike controlled solely by the TWC and tied to the design check?",
      kind: "yesno",
      effects: {
        yes: { scopeLine: "Permits to load and to strike are issued solely by the TWC, each tied to the signed-off design check for the assigned category." },
        no: { failCheck: "TWC-controlled permit-to-load and permit-to-strike regime confirmed" },
      },
      notSure: { failCheck: "TWC-controlled permit-to-load and permit-to-strike regime confirmed" },
    },
    {
      key: "register",
      label: "Does the temporary works register cover every item, including propping and trench support?",
      kind: "yesno",
      effects: {
        yes: { scopeLine: "Temporary works register covers every item, including ad-hoc propping, excavation support and hoarding, not just headline falsework." },
        no: { failCheck: "Complete temporary works register confirmed" },
      },
      notSure: { failCheck: "Complete temporary works register confirmed" },
    },
  ],
};
